import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, Download } from 'lucide-react';
import { useStore } from '@/store';
import { commonTimezones, avatarEmojis } from '@/lib/timezone';
import { signInWithGoogle, signOut, isFirebaseInitialized } from '@/lib/firebase';
import { usePageTracking } from '@/hooks/useAnalytics';

export default function Settings() {
  const { 
    me, setMe, 
    people, 
    settings, updateSettings,
    auth, setAuth,
    exportData, importData 
  } = useStore();
  
  // Track page view
  usePageTracking('settings');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  
  const handleSignIn = async () => {
    try {
      const user = await signInWithGoogle();
      setAuth({
        isAuthenticated: true,
        user: {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
        },
      });
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };
  
  const handleSignOut = async () => {
    try {
      await signOut();
      setAuth({ isAuthenticated: false, user: null });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };
  
  const handleExport = () => {
    const data = exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clocksync-backup.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        importData(content);
        alert('Data imported successfully!');
      } catch (error) {
        alert('Failed to import data. Please check the file format.');
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/" className="btn-ghost p-2">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold">Settings</h1>
      </div>
      
      {/* Profile Section */}
      <section className="card mb-6">
        <h2 className="font-semibold mb-4">Profile</h2>
        
        {/* Avatar */}
        <div className="mb-4">
          <label className="block text-sm text-text-light-muted dark:text-text-muted mb-2">Avatar</label>
          <div className="flex flex-wrap gap-2">
            {avatarEmojis.map((emoji) => (
              <button
                key={emoji}
                onClick={() => setMe({ avatar: emoji })}
                className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                  me.avatar === emoji
                    ? 'bg-primary/20 border-2 border-primary'
                    : 'bg-bg-light-hover dark:bg-bg-hover hover:bg-bg-light dark:hover:bg-bg'
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
        
        {/* Name */}
        <div className="mb-4">
          <label className="block text-sm text-text-light-muted dark:text-text-muted mb-1">Nickname</label>
          <input
            type="text"
            value={me.name}
            onChange={(e) => setMe({ name: e.target.value })}
            className="input"
          />
        </div>
        
        {/* Timezone */}
        <div className="mb-4">
          <label className="block text-sm text-text-light-muted dark:text-text-muted mb-1">Timezone</label>
          <select
            value={me.timezone}
            onChange={(e) => setMe({ timezone: e.target.value })}
            className="input"
          >
            {commonTimezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* Location */}
        <div className="mb-4">
          <label className="block text-sm text-text-light-muted dark:text-text-muted mb-1">Location</label>
          <input
            type="text"
            value={me.location}
            onChange={(e) => setMe({ location: e.target.value })}
            placeholder="City, Country"
            className="input"
          />
        </div>
        
        {/* Work Hours */}
        <div>
          <label className="block text-sm text-text-light-muted dark:text-text-muted mb-1">Work Hours</label>
          <div className="flex items-center gap-3">
            <select
              value={me.workHourStart}
              onChange={(e) => setMe({ workHourStart: Number(e.target.value) })}
              className="input"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, '0')}:00
                </option>
              ))}
            </select>
            <span className="text-text-light-muted dark:text-text-muted">to</span>
            <select
              value={me.workHourEnd}
              onChange={(e) => setMe({ workHourEnd: Number(e.target.value) })}
              className="input"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>
      
      {/* Display Settings */}
      <section className="card mb-6">
        <h2 className="font-semibold mb-4">Display</h2>
        
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <span>24-hour format</span>
            <input
              type="checkbox"
              checked={settings.use24Hour}
              onChange={(e) => updateSettings({ use24Hour: e.target.checked })}
              className="w-5 h-5 rounded bg-bg-hover"
            />
          </label>
          
          <label className="flex items-center justify-between">
            <span>Show seconds</span>
            <input
              type="checkbox"
              checked={settings.showSeconds}
              onChange={(e) => updateSettings({ showSeconds: e.target.checked })}
              className="w-5 h-5 rounded bg-bg-light-hover dark:bg-bg-hover"
            />
          </label>
          
          <label className="flex items-center justify-between">
            <span>Show tips</span>
            <input
              type="checkbox"
              checked={settings.showTips}
              onChange={(e) => updateSettings({ showTips: e.target.checked })}
              className="w-5 h-5 rounded bg-bg-light-hover dark:bg-bg-hover"
            />
          </label>
        </div>
      </section>
      
      {/* Privacy Section */}
      <section className="card mb-6">
        <h2 className="font-semibold mb-4">Privacy</h2>
        
        <div className="space-y-4">
          <label className="flex items-start justify-between gap-4">
            <div>
              <span className="block">Show my activity status</span>
              <span className="text-sm text-text-light-muted dark:text-text-muted">
                When enabled, teammates can see if you're actively using the app (Online/Away/Offline)
              </span>
            </div>
            <input
              type="checkbox"
              checked={me.showActivityStatus !== false}
              onChange={(e) => setMe({ showActivityStatus: e.target.checked })}
              className="w-5 h-5 rounded bg-bg-light-hover dark:bg-bg-hover flex-shrink-0 mt-0.5"
            />
          </label>
        </div>
        
        <div className="mt-4 p-3 bg-bg-light-hover/50 dark:bg-bg-hover/50 rounded-lg text-sm text-text-light-muted dark:text-text-muted">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 bg-green-500 rounded-full" />
            <span><strong>Online</strong> — Active in the last 5 minutes</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 bg-yellow-500 rounded-full" />
            <span><strong>Away</strong> — Active 5-30 minutes ago</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-gray-500 rounded-full" />
            <span><strong>Offline</strong> — Inactive for 30+ minutes</span>
          </div>
        </div>
      </section>
      
      {/* Account Section */}
      <section className="card mb-6">
        <h2 className="font-semibold mb-4">Account</h2>
        
        {auth.isAuthenticated && auth.user ? (
          <div>
            <div className="flex items-center gap-3 mb-4 p-3 bg-bg-light dark:bg-bg rounded-lg">
              {auth.user.photoURL ? (
                <img src={auth.user.photoURL} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  {auth.user.displayName?.charAt(0) || 'U'}
                </div>
              )}
              <div>
                <div className="font-medium">{auth.user.displayName}</div>
                <div className="text-sm text-text-light-muted dark:text-text-muted">{auth.user.email}</div>
              </div>
            </div>
            
            <button onClick={handleSignOut} className="btn-secondary w-full">
              Let Me Sign Out
            </button>
          </div>
        ) : (
          <div>
            <p className="text-sm text-text-light-muted dark:text-text-muted mb-4">
              Sign in to sync your data across devices and invite real teammates.
            </p>
            
            {isFirebaseInitialized() ? (
              <button onClick={handleSignIn} className="btn-primary w-full flex items-center justify-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Let Me Sign In
              </button>
            ) : (
              <div className="text-sm text-text-light-dim dark:text-text-dim p-3 bg-bg-light dark:bg-bg rounded-lg">
                Firebase not configured. Add your Firebase config to enable sign in.
              </div>
            )}
          </div>
        )}
      </section>
      
      {/* Data Section */}
      <section className="card mb-6">
        <h2 className="font-semibold mb-4">Data</h2>
        
        <div className="flex gap-3 mb-4">
          <button onClick={handleExport} className="btn-secondary flex-1 flex items-center justify-center gap-2">
            <Download className="w-4 h-4" />
            Export My Data
          </button>
          
          <button 
            onClick={() => fileInputRef.current?.click()} 
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
            disabled={importing}
          >
            <Upload className="w-4 h-4" />
            {importing ? 'Importing...' : 'Load My Data'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
          />
        </div>
        
        <p className="text-xs text-text-dim">
          Export your data as JSON for backup, or import a previous backup.
        </p>
      </section>
      
      {/* Team Members */}
      {people.length > 0 && (
        <section className="card">
          <h2 className="font-semibold mb-4">Team Members ({people.length})</h2>
          
          <div className="space-y-2">
            {people.map((person) => (
              <div key={person.id} className="flex items-center justify-between p-3 bg-bg-light dark:bg-bg rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{person.avatar}</span>
                  <div>
                    <div className="font-medium">{person.name}</div>
                    <div className="text-xs text-text-light-muted dark:text-text-muted">{person.timezone}</div>
                  </div>
                </div>
                <span className="text-xs text-text-light-dim dark:text-text-dim capitalize">{person.source}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
